import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Iso3D from './Iso3D';
import { IsoIllustration } from './IsoSvg';
import { Icon } from '../ui';
import '../../css/iso.css';

/**
 * 공정 4단계 — 단계를 누르면 아이소메트릭 장면이 통째로 바뀐다.
 * 5초마다 자동으로 넘어가고, 사용자가 만지면 자동 진행을 멈춘다.
 *
 * 데스크톱: 무대(좌) + 세로 단계 목록(우). 활성 단계만 설명이 펼쳐진다.
 * 모바일:   무대 → 4칸 단계 스트립 → 활성 단계 설명 카드. 가로 스크롤은 쓰지 않는다.
 */
const STEPS = [
  {
    n: '01',
    t: 'Quote',
    d: 'Your DXF is parsed for cut length and pierce count and priced on the spot. An estimator checks the drawing and confirms within 24 hours.',
    meta: 'entities · cut length · pierces',
    glyph: 'desk',
    to: '/quote',
    cta: 'Start a quote'
  },
  {
    n: '02',
    t: 'Nesting',
    d: 'Parts are arranged on the sheet to minimise scrap. You see utilisation and reusable offcuts in your account.',
    meta: '8 parts · 87.4 % · 1 sheet',
    glyph: 'nest',
    to: '/mypage/nesting',
    cta: 'See a nesting report'
  },
  {
    n: '03',
    t: 'Cutting',
    d: 'Fibre laser with nitrogen or oxygen assist, ±0.1 mm tolerance. Deburred and inspected before packing.',
    meta: 'SS304 · 2.0 mm · N₂ · kerf 0.15',
    glyph: 'laser',
    to: '/materials',
    cta: 'Materials & thicknesses'
  },
  {
    n: '04',
    t: 'Docket & dispatch',
    d: 'Parts are strapped on a pallet and loaded with the delivery docket and mill certificates. Track dispatch and download the documents anytime.',
    meta: 'mill cert · docket · tracking',
    glyph: 'truck',
    to: '/mypage/certificates',
    cta: 'Certificates by docket'
  }
];

const INTERVAL = 5200;

/** ?step=3 처럼 URL 로 단계를 지정하면 그 단계에서 멈춘 채 시작한다 (딥링크 · 캡처용). */
function initialStep() {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('step');
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n < STEPS.length ? n : null;
}

export default function IsoProcess() {
  const preset = initialStep();
  const [active, setActive] = useState(preset ?? 0);
  const [auto, setAuto] = useState(preset === null);

  useEffect(() => {
    if (!auto) return undefined;
    const id = setInterval(() => setActive(a => (a + 1) % STEPS.length), INTERVAL);
    return () => clearInterval(id);
  }, [auto]);

  const pick = i => {
    setActive(i);
    setAuto(false);
  };

  const step = STEPS[active];

  return (
    <section className="ip sec sec--alt" id="process">
      <div className="wrap">
        <div className="sec-head">
          <div className="kicker kicker--brand">How it works</div>
          <h2 className="sec-title">From drawing to docket, in four steps</h2>
          <p className="sec-lede">
            Each step is something you can see in your account — the quote, the nest, the cut and the paperwork.
          </p>
        </div>

        <div className="ip-grid">
          <div className="iso-stage ip-stage">
            <div className="iso-grid-bg" />
            <Iso3D kind="step" arg={active} live parallax maxDpr={1.5}>
              <IsoIllustration scene={step.glyph} className="ip-fallback" />
            </Iso3D>
            <div className="ip-cap mono" aria-live="polite">
              <b>Step {step.n}</b> · {step.t}
              <span className="ip-cap-meta">{step.meta}</span>
            </div>
            <button
              type="button"
              className={`ip-auto ${auto ? 'is-on' : ''}`}
              onClick={() => setAuto(a => !a)}
              aria-pressed={auto}
              title={auto ? 'Pause auto-play' : 'Resume auto-play'}
            >
              <Icon name={auto ? 'minus' : 'chevronRight'} size={13} />
              {auto ? 'auto' : 'paused'}
            </button>
          </div>

          <ol className="ip-steps">
            {STEPS.map((s, i) => (
              <li key={s.n}>
                <button
                  type="button"
                  className={`ip-step ${i === active ? 'is-on' : ''} ${i < active ? 'is-done' : ''}`}
                  aria-pressed={i === active}
                  onClick={() => pick(i)}
                  onMouseEnter={() => auto && setActive(i)}
                >
                  <span className="ip-step-n mono">{s.n}</span>
                  <span className="ip-step-body">
                    <span className="ip-step-t">{s.t}</span>
                    <span className="ip-step-d">{s.d}</span>
                    <span className="ip-step-meta mono">{s.meta}</span>
                  </span>
                  {i === active && auto && <span className="ip-step-bar" style={{ animationDuration: `${INTERVAL}ms` }} />}
                </button>
              </li>
            ))}
          </ol>

          {/* 모바일 전용 — 활성 단계 설명 (데스크톱은 목록 안에서 펼쳐진다) */}
          <div className="ip-detail rise" key={active}>
            <div className="ip-detail-head">
              <span className="ip-step-n mono is-on">{step.n}</span>
              <b>{step.t}</b>
            </div>
            <p>{step.d}</p>
            <Link to={step.to} className="btn-link sm">
              {step.cta}
              <Icon name="arrowRight" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
